import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { OrgBranding, UpdateOrgBrandingRequest } from '@brandschutz/shared';

/**
 * Globale Büro-Marke (Phase B). Wird von der App-Chrome (Sidebar-Footer),
 * der Mail-Signatur u. a. konsumiert. Bis die Daten geladen sind, liefert der
 * Hook neutrale Defaults — die UI bleibt stabil, kein Flackern leerer Felder.
 */
export const BRANDING_DEFAULTS: OrgBranding = {
  officeName: 'Ihr Ingenieurbüro',
  expertLine: 'Sachverständige:r nach BayBO',
  addressLine: '',
  contactLine: '',
  mapLabel: 'Büro',
  footerText: 'Brandschutz. Intelligent geplant.',
  emailSignature: 'Ihr Brandschutz-Team',
  logoPath: null,
  primaryColor: null,
  fontFamily: null,
  defaultMailDomain: 'bik.biz',
};

export function useBranding(): OrgBranding {
  const { data } = useQuery({
    queryKey: ['branding'],
    queryFn: async () => (await api.get<OrgBranding>('/branding')).data,
    staleTime: 1000 * 60 * 10,
  });
  return data ?? BRANDING_DEFAULTS;
}

/** Öffentliche Teil-Marke (Footer/Logo) — nutzbar vor dem Login, ohne Token. */
export interface PublicBranding {
  officeName: string;
  footerText: string;
  logoPath: string | null;
}

export function usePublicBranding(): PublicBranding {
  const { data } = useQuery({
    queryKey: ['branding-public'],
    queryFn: async () => (await api.get<PublicBranding>('/branding/public')).data,
    staleTime: 1000 * 60 * 10,
  });
  return (
    data ?? {
      officeName: BRANDING_DEFAULTS.officeName,
      footerText: BRANDING_DEFAULTS.footerText,
      logoPath: null,
    }
  );
}

export function useUpdateBranding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: UpdateOrgBrandingRequest) =>
      (await api.patch<OrgBranding>('/branding', patch)).data,
    onSuccess: (data) => queryClient.setQueryData(['branding'], data),
  });
}
